import { Injectable, inject } from '@angular/core';
import { IPromise, IQService, ITimeoutService } from 'angular';
import { Q_PORT, TIMEOUT_PORT } from '../utils';

interface CardData {
  number: string;
  cvc: string;
  exp_month: string;
  exp_year: string;
}

interface StripeTokenResponse {
  id: string;
  card: {
    id: string;
    brand: string;
    last4: string;
    fingerprint: string;
  };
}

interface AcmeTokenModel {
  id: string;
  acmeToken: string;
  creditCardBrand: string;
  ccLastFourDigits: string;
  paymentProcessorToken: string;
  fingerprint: string;
}

/**
 * StripeService (Angular 18)
 *
 * Replaces: apps/b2b-ui/common/services/stripe/stripe.service.js
 * Integrates with Stripe.js to process credit card tokens.
 */
@Injectable({ providedIn: 'root' })
export class StripeService {
  private q = inject<IQService>(Q_PORT);
  private timeout = inject<ITimeoutService>(TIMEOUT_PORT);

  model: Partial<AcmeTokenModel> = {};

  readonly card: CardData = {
    number: '',
    cvc: '',
    exp_month: '',
    exp_year: '',
  };

  readonly fields = {
    number: 'cc-number',
    cvc: 'cc-cvc',
    exp_month: 'cc-month',
    exp_year: 'cc-year',
    errors: {
      number: 'Please enter a valid credit card number',
      cvc: 'Invalid CVC code',
      exp_month: 'Please enter a valid expire month',
      exp_year: 'Please enter a valid expire year',
    },
  };

  qDefer: any = false;

  // ─── Initialization ───────────────────────────────────────────────────────

  init(): boolean {
    return true;
  }

  // ─── Card management ──────────────────────────────────────────────────────

  clear(): void {
    this.card.number = '';
    this.card.cvc = '';
    this.card.exp_month = '';
    this.card.exp_year = '';
    this.model = {};
  }

  // ─── Token generation ─────────────────────────────────────────────────────

  generateToken(): string {
    const cc = this.card.number.replace(/ /g, '');
    const first = cc.substr(0, 6);
    const last = cc.substr(cc.length - 4, 4);
    return first + last;
  }

  processBrand(brand: string): string {
    return /American Express/.test(brand) ? 'AmericanExpress' : brand;
  }

  // ─── Stripe integration ───────────────────────────────────────────────────

  process(): IPromise<unknown> {
    this.qDefer = this.q.defer();

    (window as any).Stripe?.setPublishableKey((window as any).CONFIG?.stripe?.key);
    (window as any).Stripe?.card?.createToken(
      {
        number: this.card.number,
        cvc: this.card.cvc,
        exp_month: this.card.exp_month,
        exp_year: this.card.exp_year,
      },
      (status: number, response: unknown) => {
        this.onResponse(status, response);
      }
    );

    return this.qDefer.promise;
  }

  onResponse(status: number, response: any): void {
    console.log('StripeService::onResponse', status, response);
    if (status < 400) {
      this.successResponse(response);
    } else {
      this.errorResponse(response);
    }
  }

  private successResponse(response: StripeTokenResponse): void {
    console.log('StripeService::success::response', this, response);

    this.model = {
      id: response.card.id,
      acmeToken: this.generateToken(),
      creditCardBrand: this.processBrand(response.card.brand),
      ccLastFourDigits: response.card.last4,
      paymentProcessorToken: response.id,
      fingerprint: response.card.fingerprint,
    };

    this.qDefer.resolve({ data: { payload: this.model } });
  }

  private errorResponse(response: any): void {
    console.log('StripeService::error::response', this, response);
    const validator: any = {};

    switch (response.error.param) {
      case 'number':
        validator.field = this.fields.number;
        validator.len = { message: this.fields.errors.number, test: { value: '' } };
        break;
      case 'cvc':
        validator.field = this.fields.cvc;
        validator.len = { message: this.fields.errors.cvc, test: { value: '' } };
        break;
      case 'exp_month':
        validator.field = this.fields.exp_month;
        validator.len = { message: this.fields.errors.exp_month, test: { value: '' } };
        break;
      case 'exp_year':
        validator.field = this.fields.exp_year;
        validator.len = { message: this.fields.errors.exp_year, test: { value: '' } };
        break;
    }

    this.qDefer.reject({ data: { error: response.error.message, validator } });
  }
}
